/**
 * Slot Machine Skins Admin - List Page
 *
 * Displays all slot machine skins with CRUD operations.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Edit,
  Copy,
  Star,
  StarOff,
  MoreVertical,
  Search,
  Loader2,
  Archive,
  CheckCircle,
  Clock,
  Dices,
} from 'lucide-react';
import type { SkinStatus } from '../../public/deals/components/slot-machine/themes/skin-types';

// =============================================================================
// Types
// =============================================================================

interface SkinListItem {
  id: string;
  name: string;
  slug: string;
  status: SkinStatus;
  is_default: boolean;
  version: string;
  symbol_count?: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
}

// =============================================================================
// Component
// =============================================================================

export default function SlotSkins() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<SkinStatus | 'all'>('all');
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // =============================================================================
  // Queries
  // =============================================================================

  const { data: skinsData, isLoading } = useQuery({
    queryKey: ['admin-slot-skins', searchQuery, statusFilter],
    queryFn: async () => {
      const response = await fetch('/api/slot-skins?' + new URLSearchParams({
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
      }));
      if (!response.ok) throw new Error('Failed to fetch skins');
      return response.json() as Promise<{ skins: SkinListItem[]; total: number }>;
    },
  });

  // =============================================================================
  // Mutations
  // =============================================================================

  const createSkinMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/slot-skins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'New Slot Machine',
          slug: `slot-${Date.now()}`,
        }),
      });
      if (!response.ok) throw new Error('Failed to create skin');
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skins'] });
      navigate(`/admin/slot-skins/${data.skin.id}`);
    },
  });

  const duplicateSkinMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/slot-skins/${id}/duplicate`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to duplicate skin');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skins'] });
      setActionMenuId(null);
    },
  });

  const deleteSkinMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/slot-skins/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete skin');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skins'] });
      setDeleteConfirmId(null);
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/slot-skins/${id}/set-default`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to set default');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skins'] });
      setActionMenuId(null);
    },
  });

  // =============================================================================
  // Helpers
  // =============================================================================

  const getStatusBadge = (status: SkinStatus, isDefault: boolean) => {
    const baseClasses = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium';

    if (isDefault) {
      return (
        <span className={`${baseClasses} bg-amber-100 text-amber-800`}>
          <Star size={12} /> Default
        </span>
      );
    }

    switch (status) {
      case 'published':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            <CheckCircle size={12} /> Published
          </span>
        );
      case 'draft':
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            <Clock size={12} /> Draft
          </span>
        );
      case 'archived':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            <Archive size={12} /> Archived
          </span>
        );
    }
  };

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Slot Machine Skins</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create and manage slot machine themes and configurations
          </p>
        </div>
        <button
          onClick={() => createSkinMutation.mutate()}
          disabled={createSkinMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {createSkinMutation.isPending ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Plus size={18} />
          )}
          Create New Skin
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search skins..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as SkinStatus | 'all')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-gray-400" size={32} />
          </div>
        ) : !skinsData?.skins?.length ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Dices size={48} className="mb-4 opacity-50" />
            <p className="text-lg font-medium">No slot machine skins yet</p>
            <p className="text-sm">Create your first skin to get started</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Skin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Symbols
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {skinsData.skins.map((skin) => (
                <tr
                  key={skin.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/admin/slot-skins/${skin.id}`)}
                >
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{skin.name}</div>
                      <div className="text-sm text-gray-500">/{skin.slug}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(skin.status, skin.is_default)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    v{skin.version}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {skin.symbol_count ?? 0} symbols
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(skin.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setActionMenuId(actionMenuId === skin.id ? null : skin.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <MoreVertical size={16} />
                      </button>
                      {actionMenuId === skin.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                          <button
                            onClick={() => navigate(`/admin/slot-skins/${skin.id}`)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Edit size={14} /> Edit
                          </button>
                          <button
                            onClick={() => duplicateSkinMutation.mutate(skin.id)}
                            disabled={duplicateSkinMutation.isPending}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Copy size={14} /> Duplicate
                          </button>
                          {!skin.is_default && skin.status === 'published' && (
                            <button
                              onClick={() => setDefaultMutation.mutate(skin.id)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Star size={14} /> Set as Default
                            </button>
                          )}
                          {skin.is_default && (
                            <button
                              disabled
                              className="w-full px-4 py-2 text-left text-sm text-gray-400 flex items-center gap-2"
                            >
                              <StarOff size={14} /> Cannot delete default
                            </button>
                          )}
                          {!skin.is_default && (
                            <button
                              onClick={() => {
                                setActionMenuId(null);
                                setDeleteConfirmId(skin.id);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">Delete Slot Machine Skin?</h3>
            <p className="text-gray-500 mb-6">
              This will permanently delete this skin and all its assets. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteSkinMutation.mutate(deleteConfirmId)}
                disabled={deleteSkinMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleteSkinMutation.isPending && <Loader2 size={16} className="animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
